/**
 * NotificationPrimer — our OWN "turn on notifications?" screen, shown BEFORE the
 * OS permission prompt.
 *
 * Why this exists: iOS gives an app exactly one shot at the system prompt, and a
 * denial is near-permanent (the user has to dig into Settings to undo it). So we
 * never fire the OS prompt cold. We ask on our own UI first; only if the player
 * taps Enable do we trigger the real prompt. A "Not now" here is fully
 * recoverable, and costs us nothing.
 *
 * show() resolves true when the player taps Enable, false otherwise. The caller
 * (main.ts) then drives notificationService.requestPermissionAndRegister().
 *
 * Styled to match ErrorModal (neon-noir dark panel).
 */

const STYLE_ID = 'sdl-notif-primer-styles';

export class NotificationPrimer {
  public element: HTMLElement;
  private visible = false;
  private resolver: ((enabled: boolean) => void) | null = null;

  constructor() {
    NotificationPrimer.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-nprime-host';
    this.element.innerHTML = `
      <div class="sdl-nprime-backdrop"></div>
      <div class="sdl-nprime-panel">
        <div class="sdl-nprime-icon">🔔</div>
        <div class="sdl-nprime-title">STAY IN THE GAME</div>
        <div class="sdl-nprime-msg">Get a heads up when your crew messages you and when your free Stacks are ready to claim.</div>
        <button class="sdl-nprime-btn sdl-nprime-yes" data-yes>ENABLE NOTIFICATIONS</button>
        <button class="sdl-nprime-btn sdl-nprime-no" data-no>NOT NOW</button>
      </div>
    `;
    document.body.appendChild(this.element);

    this.element.querySelector('[data-yes]')!.addEventListener('click', () => this.resolve(true));
    this.element.querySelector('[data-no]')!.addEventListener('click', () => this.resolve(false));
    // Backdrop tap = "not now" (recoverable, so dismissive is safe).
    this.element.querySelector('.sdl-nprime-backdrop')!.addEventListener('click', () => this.resolve(false));
  }

  /** Show the primer. Resolves true if the player chose to enable. */
  show(): Promise<boolean> {
    if (this.visible) return Promise.resolve(false);
    this.visible = true;
    this.element.classList.add('visible');
    return new Promise<boolean>((res) => { this.resolver = res; });
  }

  private resolve(enabled: boolean): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
    const r = this.resolver;
    this.resolver = null;
    r?.(enabled);
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-nprime-host {
        position: fixed; inset: 0;
        z-index: 905;
        display: none;
        align-items: center; justify-content: center;
      }
      .sdl-nprime-host.visible { display: flex; }
      .sdl-nprime-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.75);
        backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px);
      }
      .sdl-nprime-panel {
        position: relative;
        width: 300px;
        padding: 26px 22px 16px;
        background: var(--concrete, #111114);
        border: 1px solid rgba(0,229,255,0.4);
        border-radius: 20px;
        display: flex; flex-direction: column; align-items: center;
        gap: 8px;
        box-shadow: 0 18px 50px rgba(0,0,0,0.6);
        animation: sdl-nprime-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      @keyframes sdl-nprime-in {
        0% { opacity: 0; transform: scale(0.8); }
        100% { opacity: 1; transform: scale(1); }
      }
      .sdl-nprime-icon {
        font-size: 40px;
        line-height: 1;
        margin-bottom: 2px;
      }
      .sdl-nprime-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 26px; letter-spacing: 1px; text-align: center;
        color: #fff;
      }
      .sdl-nprime-msg {
        font-family: 'Rajdhani', sans-serif; font-weight: 500;
        font-size: 14px; line-height: 1.45; text-align: center;
        color: rgba(255,255,255,0.72);
        margin-bottom: 10px;
      }
      .sdl-nprime-btn {
        width: 100%;
        padding: 13px 0;
        border: none; border-radius: 13px;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 19px; letter-spacing: 2px;
        cursor: pointer;
        transition: transform 0.1s, background 0.15s, filter 0.15s;
      }
      .sdl-nprime-yes {
        background: linear-gradient(135deg, #00e5ff, #00a0c0);
        color: #04141a;
        margin-bottom: 4px;
      }
      .sdl-nprime-yes:active { transform: scale(0.97); filter: brightness(1.1); }
      .sdl-nprime-no {
        background: rgba(255,255,255,0.08);
        color: rgba(255,255,255,0.66);
      }
      .sdl-nprime-no:active { transform: scale(0.97); background: rgba(255,255,255,0.14); }
    `;
    document.head.appendChild(style);
  }
}
